import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, Mail, User, Globe, MessageSquare, ChevronRight } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const WriteForUs = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    website: '',
    topic: '',
    pitch: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formBody = {
        'form-name': 'write-for-us',
        ...formData
      };

      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formBody).toString()
      });

      if (response.ok) {
        window.location.href = '/success';
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('There was an error submitting your application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative bg-gray-900 text-white">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1455390582262-044cdead277a"
            alt="Writing"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/50 to-black/80"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
          <div className="mb-4">
            <Link to="/blog" className="inline-flex items-center text-white/90 hover:text-white">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Blog
            </Link>
          </div>
          
          <div className="max-w-3xl">
            <h1 className="text-4xl font-bold mb-4">Write for TravelCord</h1>
            <p className="text-lg text-white/90">
              Share your Uganda travel experiences, insights, and expertise with our global community of travelers.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Submission Guidelines</h2>
              
              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">What We're Looking For</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <ChevronRight className="h-5 w-5 text-ta-green mr-2 flex-shrink-0 mt-0.5" />
                      <span>Original, well-researched articles about Uganda travel experiences</span>
                    </li>
                    <li className="flex items-start">
                      <ChevronRight className="h-5 w-5 text-ta-green mr-2 flex-shrink-0 mt-0.5" />
                      <span>Practical tips and insights for fellow travelers</span>
                    </li>
                    <li className="flex items-start">
                      <ChevronRight className="h-5 w-5 text-ta-green mr-2 flex-shrink-0 mt-0.5" />
                      <span>Cultural stories and local perspectives</span>
                    </li>
                    <li className="flex items-start">
                      <ChevronRight className="h-5 w-5 text-ta-green mr-2 flex-shrink-0 mt-0.5" />
                      <span>High-quality photographs to accompany your writing</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Article Requirements</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <ChevronRight className="h-5 w-5 text-ta-green mr-2 flex-shrink-0 mt-0.5" />
                      <span>1,000-2,000 words in length</span>
                    </li>
                    <li className="flex items-start">
                      <ChevronRight className="h-5 w-5 text-ta-green mr-2 flex-shrink-0 mt-0.5" />
                      <span>Original content not published elsewhere</span>
                    </li>
                    <li className="flex items-start">
                      <ChevronRight className="h-5 w-5 text-ta-green mr-2 flex-shrink-0 mt-0.5" />
                      <span>Proper attribution for any quotes or references</span>
                    </li>
                    <li className="flex items-start">
                      <ChevronRight className="h-5 w-5 text-ta-green mr-2 flex-shrink-0 mt-0.5" />
                      <span>At least 3-5 high-quality images</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Link Building Opportunities</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <ChevronRight className="h-5 w-5 text-ta-green mr-2 flex-shrink-0 mt-0.5" />
                      <span>Author bio with link to your website or social profiles</span>
                    </li>
                    <li className="flex items-start">
                      <ChevronRight className="h-5 w-5 text-ta-green mr-2 flex-shrink-0 mt-0.5" />
                      <span>Relevant contextual links within your article (max 2 links)</span>
                    </li>
                    <li className="flex items-start">
                      <ChevronRight className="h-5 w-5 text-ta-green mr-2 flex-shrink-0 mt-0.5" />
                      <span>Cross-promotion on our social media channels</span>
                    </li>
                    <li className="flex items-start">
                      <ChevronRight className="h-5 w-5 text-ta-green mr-2 flex-shrink-0 mt-0.5" />
                      <span>Featured author spotlight for regular contributors</span>
                    </li>
                  </ul>
                </div>
              </div>

              <form 
                name="write-for-us"
                method="POST"
                data-netlify="true"
                onSubmit={handleSubmit}
                className="space-y-6"
              >
                <input type="hidden" name="form-name" value="write-for-us" />
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-ta-green focus:ring-ta-green"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-ta-green focus:ring-ta-green"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Website/Blog (Optional)</label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="url"
                      name="website"
                      value={formData.website}
                      onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                      className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-ta-green focus:ring-ta-green"
                      placeholder="https://"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Proposed Topic/Title *</label>
                  <input
                    type="text"
                    name="topic"
                    required
                    value={formData.topic}
                    onChange={(e) => setFormData({ ...formData, topic: e.target.value })}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-ta-green focus:ring-ta-green"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Article Pitch *</label>
                  <div className="relative">
                    <MessageSquare className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                    <textarea
                      name="pitch"
                      required
                      value={formData.pitch}
                      onChange={(e) => setFormData({ ...formData, pitch: e.target.value })}
                      rows={6}
                      className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-ta-green focus:ring-ta-green"
                      placeholder="Tell us about your proposed article..."
                    ></textarea>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-3 bg-ta-green text-white rounded-lg hover:bg-ta-green-dark transition-colors flex items-center justify-center"
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Submitting...
                    </>
                  ) : (
                    <>
                      Submit Pitch
                      <ChevronRight className="ml-2 h-5 w-5" />
                    </>
                  )}
                </button>
              </form>
            </div>

            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Benefits</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <ChevronRight className="h-5 w-5 text-ta-green mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-600">Reach a global audience of travelers</span>
                    </li>
                    <li className="flex items-start">
                      <ChevronRight className="h-5 w-5 text-ta-green mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-600">Build your portfolio</span>
                    </li>
                    <li className="flex items-start">
                      <ChevronRight className="h-5 w-5 text-ta-green mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-600">Competitive compensation</span>
                    </li>
                    <li className="flex items-start">
                      <ChevronRight className="h-5 w-5 text-ta-green mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-600">Professional editing support</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Popular Topics</h3>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">Wildlife</span>
                    <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">Culture</span>
                    <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">Adventure</span>
                    <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">Food</span>
                    <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">Photography</span>
                    <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">Travel Tips</span>
                    <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">Gorilla Trekking</span>
                    <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">Safari Guides</span>
                    <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">Self-Drive</span>
                  </div>
                </div>

                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact</h3>
                  <p className="text-gray-600 mb-4">
                    Have questions about writing for us? Feel free to reach out:
                  </p>
                  <a 
                    href="mailto:writers@travelcord.guide"
                    className="text-ta-green hover:text-ta-green-dark"
                  >
                    writers@travelcord.guide
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default WriteForUs;